import {
  IconChevronRight,
  IconUser,
  IconSettings,
  IconFlask,
  IconLogout,
  IconBuilding,
  IconCheck,
} from '@tabler/icons-react';
import { Avatar, Group, Text, UnstyledButton, Menu } from '@mantine/core';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import classes from './UserButton.module.css';
import { useUserStore } from '../../store/userStore';
import { FEATURE_FLAGS } from '../../config/featureFlags';
import { unsubscribeThisDevice } from '../../hooks/usePushApi';
import { useDevice } from '../../hooks/useDevice';
import { useAnalytics, AnalyticsEvents } from '../../analytics';
import { switchCompany, useMyCompanies } from '../../hooks/useAuthApi';
import { showToast } from '../../utils/toast';
import { getErrorMessage } from '../../utils/api';

export function UserButton({ collapsed }: { collapsed?: boolean } = {}) {
  const navigate = useNavigate();
  const logout = useUserStore((s) => s.logout);
  const user = useUserStore((s) => s.user);
  const { t } = useTranslation();
  const deviceType = useDevice();
  const { track } = useAnalytics();
  // «Сменить компанию» — если у этой почты учётки в нескольких компаниях.
  const { data: companies = [] } = useMyCompanies();

  const handleSwitch = (tenantId: string) =>
    switchCompany(tenantId).catch((e) =>
      showToast({ title: t('common.error'), message: getErrorMessage(e), color: 'red' }),
    );

  const handleLogout = async () => {
    track(AnalyticsEvents.USER_LOGGED_OUT);
    // Отписать устройство: на одном адресе следующим может войти человек другой компании.
    // Выход по отвергнутому токену (401) отписывает сам (`logout({ isSessionRejected })`).
    await unsubscribeThisDevice();
    const isDemoUser = !!user?.email?.endsWith('@demo.skillscape.ru');
    logout();
    // Из демо — обратно к выбору роли (сменить роль в той же песочнице). Полной загрузкой:
    // переход роутером проигрывает гонку защищённому маршруту, и тот уводит на /auth.
    if (isDemoUser) window.location.assign('/demo');
  };

  return (
    <Menu
      shadow="md"
      width={deviceType === 'mobile' ? 180 : 200}
      position={deviceType === 'mobile' ? 'bottom-end' : 'left-end'}
      offset={deviceType === 'mobile' ? 8 : 0}
      closeOnClickOutside
      closeOnEscape
    >
      <Menu.Target>
        <UnstyledButton
          className={classes.user}
          style={collapsed ? { display: 'flex', justifyContent: 'center' } : undefined}
          title={collapsed ? user?.name || undefined : undefined}
        >
          {collapsed ? (
            <Avatar src={user?.avatar} radius="xl" />
          ) : (
            <Group gap="xs">
              <Avatar src={user?.avatar} radius="xl" />

              <div style={{ flex: 1, maxWidth: 140 }}>
                <Text
                  size="sm"
                  fw={500}
                  style={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    maxWidth: '100%',
                  }}
                >
                  {user?.name || '—'}
                </Text>

                <Text
                  c="dimmed"
                  size="xs"
                  style={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    maxWidth: '100%',
                  }}
                >
                  {user?.email || '—'}
                </Text>
              </div>

              <IconChevronRight size={14} stroke={1.5} />
            </Group>
          )}
        </UnstyledButton>
      </Menu.Target>

      <Menu.Dropdown>
        <Menu.Label>{t('userMenu.profile')}</Menu.Label>
        <Menu.Item leftSection={<IconUser size={14} />} onClick={() => navigate('/profile')}>
          {t('userMenu.myProfile')}
        </Menu.Item>
        <Menu.Item leftSection={<IconSettings size={14} />} onClick={() => navigate('/settings')}>
          {t('userMenu.settings')}
        </Menu.Item>
        {/* Бета-функций сейчас нет — пункт вернётся вместе с первой. */}
        {FEATURE_FLAGS.length > 0 && (
          <Menu.Item
            leftSection={<IconFlask size={14} />}
            onClick={() => navigate('/settings/features')}
          >
            {t('featureFlags.title')}
          </Menu.Item>
        )}

        {companies.length > 1 && (
          <>
            <Menu.Divider />
            <Menu.Label>{t('userMenu.switchCompany')}</Menu.Label>
            {companies.map((c) => (
              <Menu.Item
                key={c.id}
                leftSection={c.isCurrent ? <IconCheck size={14} /> : <IconBuilding size={14} />}
                disabled={c.isCurrent}
                onClick={() => handleSwitch(c.id)}
              >
                {c.name}
              </Menu.Item>
            ))}
            <Menu.Divider />
          </>
        )}

        {/*<Menu.Label>{t('userMenu.security')}</Menu.Label>*/}
        {/*<Menu.Item */}
        {/*  leftSection={<IconKey size={14} />}*/}
        {/*  onClick={() => navigate('/change-password')}*/}
        {/*>*/}
        {/*  {t('userMenu.changePassword')}*/}
        {/*</Menu.Item>*/}

        {/*<Menu.Divider />*/}

        <Menu.Item leftSection={<IconLogout size={14} />} color="red" onClick={handleLogout}>
          {t('userMenu.logout')}
        </Menu.Item>
      </Menu.Dropdown>
    </Menu>
  );
}
